import { z } from 'zod';

import type { EquipmentCategory } from '@/shared/validation/fetch.validation';

export const discountSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  percentage: z
    .number()
    .min(0, 'Percentage must be 0 or greater')
    .max(100, 'Percentage must be 100 or less')
    .nullable(),
});
export type Discount = z.infer<typeof discountSchema>;

export const discountsSchema = z
  .array(discountSchema)
  .min(1, 'At least one discount is required')
  .max(5, 'You can only add up to 5 discounts');
export type Discounts = z.infer<typeof discountsSchema>;

export const itemDiscountSchema = z.object({
  itemId: z.number(),
  percentage: z
    .number()
    .min(0, 'Percentage must be 0 or greater')
    .max(100, 'Percentage must be 100 or less')
    .nullable(),
});
export type ItemDiscount = z.infer<typeof itemDiscountSchema>;

export const configureIntentSchema = z.enum(['goCart', 'goHome', 'goDuplicate', 'goSave']);
export type ConfigureIntent = z.infer<typeof configureIntentSchema>;

const configureFormSchema = z.object({
  productId: z.string().min(1, 'Product ID is required'),
  selected: z.string().optional(),
  code: z.string().optional(),
  discounts: z.array(discountSchema),
  itemDiscounts: z.array(itemDiscountSchema),
  intent: configureIntentSchema.optional(),
});

const configureFormWithDiscountsSchema = z.object({
  productId: z.string().min(1, 'Product ID is required'),
  selected: z.string().optional(),
  code: z.string().optional(),
  discounts: z
    .array(discountSchema)
    .min(1, 'At least one discount is required')
    .max(5, 'You can only add up to 5 discounts'),
  itemDiscounts: z.array(itemDiscountSchema),
  intent: configureIntentSchema.optional(),
});

export const buildConfigureFormSchema = (
  categories: EquipmentCategory[],
  hasItemsWithPrice: boolean,
) =>
  (hasItemsWithPrice ? configureFormWithDiscountsSchema : configureFormSchema).check((ctx) => {
    const selectedIds = ctx.value.selected
      ?.split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => Number(s))
      .filter((id) => Number.isFinite(id));

    const errorItems = categories
      .flatMap((category) => category.items)
      .filter((item) => item.status === 'error');

    if (errorItems.length > 0) {
      const errorNames = errorItems
        .map((item) => item.title ?? item.ident ?? `#${item.id}`)
        .join(', ');

      ctx.issues.push({
        code: 'custom',
        message: `Equipment configuration error: ${errorNames}`,
        path: ['selected'],
        input: ctx.value,
      });
    }

    // Check each mandatory category has at least one selected item
    const mandatoryCategories = categories.filter((category) => category.mandatory);

    for (const category of mandatoryCategories) {
      const categoryItemIds = category.items.map((item) => item.id);
      const hasSelectedItem = categoryItemIds.some((itemId) => selectedIds?.includes(itemId));

      if (!hasSelectedItem) {
        ctx.issues.push({
          code: 'custom',
          message: `${category.id} - Required`,
          path: ['selected'],
          input: ctx.value,
          params: {
            categoryId: category.id,
          },
        });
      }
    }
  });

export type ConfigureFormValues = z.infer<ReturnType<typeof buildConfigureFormSchema>>;

export const quoteFormSchema = z
  .object({
    title: z.enum(['mr', 'ms', 'mrs', 'mx']),
    firstName: z.string().min(1, 'Required'),
    lastName: z.string().min(1, 'Required'),
    customerType: z.enum(['business', 'private']).optional(),
    email: z.email(),
    phone: z.string().min(1, 'Required'),
    companyName: z.string().optional(),
    vatNumber: z.string().optional(),
    marketingEmail: z.enum(['yes', 'no']),
    marketingPhone: z.enum(['yes', 'no']),
    address1: z.string().min(1, 'Required'),
    address2: z.string().optional(),
    country: z.string().trim().min(1, 'Required'),
    postalCode: z.string().min(1, 'Required'),
    city: z.string().min(1, 'Required'),
    note: z.string().optional(),
  })
  .check((ctx) => {
    if (ctx.value.customerType === 'business') {
      if (!ctx.value.companyName) {
        ctx.issues.push({
          code: 'custom',
          message: 'Required',
          path: ['companyName'],
          input: ctx.value,
        });
      }

      if (!ctx.value.vatNumber) {
        ctx.issues.push({
          code: 'custom',
          message: 'Required',
          path: ['vatNumber'],
          input: ctx.value,
        });
      }
    }

    if (!ctx.value.country) {
      ctx.issues.push({
        code: 'custom',
        message: 'Required',
        path: ['country'],
        input: ctx.value,
      });
    }

    if (!ctx.value.customerType) {
      ctx.issues.push({
        code: 'custom',
        message: 'Required',
        path: ['customerType'],
        input: ctx.value,
      });
    }
  });

export type QuoteFormValues = z.infer<typeof quoteFormSchema>;

export const cartFormSchema = z.object({
  discount: z.number().min(0).max(100),
});
export type CartFormValues = z.infer<typeof cartFormSchema>;
